import { useState, useMemo } from "react";
import {
  Check,
  X,
  Clock,
  Search,
  Filter,
  Download,
  ChevronLeft,
  ChevronRight,
  AlertTriangle,
  Eye,
  MessageSquare,
  RefreshCw,
  TrendingUp,
  TrendingDown,
  ArrowLeft,
} from "lucide-react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { toast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { useTrades } from "@/hooks";
import {
  iconMap,
  TradeStatus,
  TradeType,
  type IconKey,
  type Trade,
} from "@/interfaces/trade";
import { formatCurrency, normalizeCurrency } from "@/utils";
import Loader from "@/components/loader/Loader";
import { ErrorState } from "@/components/ErrorState";
import TradeCommunicationDialog from "@/components/shared/dispute/TradeCommunicationDialog";

const AdminTrades = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [dateRange, setDateRange] = useState("7d");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedTrade, setSelectedTrade] = useState<Trade | null>(null);
  const [showTradeModal, setShowTradeModal] = useState(false);

  const [communicationOpen, setCommunicationOpen] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const itemsPerPage = 10;

  const navigate = useNavigate();

  const { data: tradesData, isLoading, isError, refetch } = useTrades();

  console.log("API Trades:", tradesData);

  // Filter and sort trades
  const filteredTrades = useMemo(() => {
    return tradesData?.data.filter((trade) => {
      const q = searchQuery.toLowerCase();

      const matchesSearch =
        trade.id?.toLowerCase().includes(q) ||
        trade.buyer?.username?.toLowerCase().includes(q) ||
        trade.seller?.username?.toLowerCase().includes(q) ||
        trade.seller?.merchantName?.toLowerCase().includes(q) ||
        trade.buyer?.merchantName?.toLowerCase().includes(q) ||
        trade.advertisement?.asset?.currency?.toLowerCase().includes(q);

      const matchesStatus =
        statusFilter === "all" || trade.status === statusFilter;
      const matchesType =
        typeFilter === "all" || trade?.advertisement?.type === typeFilter;

      return matchesSearch && matchesStatus && matchesType;
    });
  }, [tradesData, searchQuery, statusFilter, typeFilter]);

  // Pagination
  const totalPages = Math.ceil((filteredTrades?.length || 0) / itemsPerPage);
  const paginatedTrades = filteredTrades?.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  const getStatusBadge = (status: TradeStatus) => {
    switch (status) {
      case TradeStatus.PAID:
      case TradeStatus.RELEASED:
        return (
          <span className="flex items-center gap-1 px-2 py-1 rounded text-xs bg-emerald-500/10 text-emerald-500">
            <Check className="w-3 h-3" /> Completed
          </span>
        );
      // case TradStatus.ACTIVE:
      case TradeStatus.PENDING:
        return (
          <span className="flex items-center gap-1 px-2 py-1 rounded text-xs bg-primary/10 text-primary">
            <Clock className="w-3 h-3" /> Active
          </span>
        );
      case TradeStatus.DISPUTED:
        return (
          <span className="flex items-center gap-1 px-2 py-1 rounded text-xs bg-red-500/10 text-red-500">
            <AlertTriangle className="w-3 h-3" /> Disputed
          </span>
        );
      case TradeStatus.CANCELLED:
        return (
          <span className="flex items-center gap-1 px-2 py-1 rounded text-xs bg-muted text-muted-foreground">
            <X className="w-3 h-3" /> Cancelled
          </span>
        );
      default:
        return <span className="text-muted-foreground">{status}</span>;
    }
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await refetch();
    setTimeout(() => {
      setIsRefreshing(false);
      toast({
        title: "Refreshed",
        description: "Trade data has been updated.",
      });
    }, 500);
  };

  const newTrades = filteredTrades ?? [];

  const handleExport = () => {
    const csvContent = [
      [
        "Trade ID",
        "Type",
        "Buyer",
        "Seller",
        "Amount",
        "Crypto",
        "Price",
        "Status",
        "Time",
      ].join(","),
      ...newTrades.map((t) =>
        [
          t.id,
          t?.advertisement?.type,
          t.buyer?.firstName + " " + t.buyer?.lastName,
          t.seller?.firstName + " " + t.seller?.lastName,
          t.amount,
          t.advertisement?.asset?.currency,
          t.price,
          t.status,
          t.createdAt,
        ].join(","),
      ),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `trades-export-${new Date().toISOString().split("T")[0]}.csv`;
    a.click();

    toast({
      title: "Export Complete",
      description: `${filteredTrades?.length} trades exported to CSV.`,
    });
  };

  const handleResolveDispute = (trade: any, favor: "buyer" | "seller") => {
    toast({
      title: "Dispute Resolved",
      description: `Trade ${trade.id} resolved in favor of ${favor}.`,
    });
    setShowTradeModal(false);
  };

  if (isLoading) return <Loader type="bounce" />;

  if (isError)
    return (
      <ErrorState
        onRetry={() => {
          refetch();
        }}
      />
    );
  return (
    <div className="min-h-screen bg-background px-4 lg:px-6">
      <div className="max-w-7xl mx-auto">
        <Button variant="ghost" onClick={() => navigate(-1)}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-foreground">
              Trade Management
            </h1>
            <p className="text-muted-foreground mt-1">
              Monitor and manage all platform trades
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleRefresh}
              disabled={isRefreshing}
              className="p-2 bg-card rounded-lg text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
            >
              <RefreshCw
                className={`w-5 h-5 ${isRefreshing ? "animate-spin" : ""}`}
              />
            </button>
            <button
              onClick={handleExport}
              className="flex items-center gap-2 px-4 py-2 bg-card text-foreground rounded-lg hover:bg-muted transition-colors"
            >
              <Download className="w-4 h-4" />
              Export
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {tradesData?.stats?.map((stat, i) => {
            const Icon = iconMap[stat.icon as IconKey];
            return (
              <div key={i}>
                <div className="nex-card p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                      {Icon && <Icon className="w-5 h-5 text-primary" />}
                    </div>
                    <span
                      className={`text-xs flex items-center gap-1 ${stat.positive ? "text-emerald-500" : "text-red-500"}`}
                    >
                      {stat.positive ? (
                        <TrendingUp className="w-3 h-3" />
                      ) : (
                        <TrendingDown className="w-3 h-3" />
                      )}
                      {stat.change}
                    </span>
                  </div>
                  <p className="text-2xl font-bold text-foreground">
                    {stat.value}
                  </p>
                  <p className="text-xs text-muted-foreground">{stat.label}</p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Volume Chart */}
        <div className="nex-card p-6 mb-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="font-semibold text-foreground text-lg">
                Trading Volume
              </h3>
              <p className="text-sm text-muted-foreground">
                Last 7 days activity
              </p>
            </div>
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              className="px-4 py-2 bg-card border border-border rounded-lg text-sm text-foreground"
            >
              <option value="24h">Last 24 Hours</option>
              <option value="7d">Last 7 Days</option>
              <option value="30d">Last 30 Days</option>
            </select>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={tradesData?.volumeData}>
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="hsl(var(--border))"
                />
                <XAxis
                  dataKey="date"
                  stroke="hsl(var(--muted-foreground))"
                  fontSize={12}
                />
                <YAxis
                  stroke="hsl(var(--muted-foreground))"
                  fontSize={12}
                  tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "hsl(var(--card))",
                    border: "1px solid hsl(var(--border))",
                    borderRadius: "8px",
                  }}
                  itemStyle={{ color: "hsl(var(--foreground))" }}
                  formatter={(value: number) => [
                    `$${value.toLocaleString()}`,
                    "Volume",
                  ]}
                />
                <Line
                  type="monotone"
                  dataKey="volume"
                  stroke="#F0B90B"
                  strokeWidth={2}
                  dot={{ fill: "#F0B90B" }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col lg:flex-row lg:items-center gap-4 mb-6">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by trade ID, buyer, or seller..."
              className="w-full pl-10 pr-4 py-2 bg-card border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary"
            />
          </div>
          <div className="flex items-center gap-3">
            <Filter className="w-4 h-4 text-muted-foreground" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 bg-card border border-border rounded-lg text-sm text-foreground"
            >
              <option value="all">All Status</option>
              <option value={TradeStatus.PENDING}>Active</option>
              <option value={TradeStatus.PAID}>Completed</option>
              <option value={TradeStatus.DISPUTED}>Disputed</option>
              <option value={TradeStatus.CANCELLED}>Cancelled</option>
              <option value={TradeStatus.EXPIRED}>Expired</option>
            </select>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="px-4 py-2 bg-card border border-border rounded-lg text-sm text-foreground"
            >
              <option value="all">All Types</option>
              <option value={TradeType.BUY}>Buy</option>
              <option value={TradeType.SELL}>Sell</option>
            </select>
          </div>
        </div>

        {/* Trades Table */}
        <div className="nex-card overflow-hidden">
          {isLoading ? (
            <div className="p-8 text-center text-muted-foreground">
              <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full mx-auto mb-4" />
              Loading trades...
            </div>
          ) : (
            <>
              <div className="overflow-x-auto -mx-4 sm:-mx-0">
                <table className="w-full min-w-[900px] sm:min-w-0">
                  <thead className="bg-card">
                    <tr>
                      <th className="text-left text-xs font-medium text-muted-foreground py-3 px-4 whitespace-nowrap">
                        Trade ID
                      </th>
                      <th className="text-left text-xs font-medium text-muted-foreground py-3 px-4 whitespace-nowrap">
                        Type
                      </th>
                      <th className="text-left text-xs font-medium text-muted-foreground py-3 px-4 whitespace-nowrap">
                        Buyer
                      </th>
                      <th className="text-left text-xs font-medium text-muted-foreground py-3 px-4 whitespace-nowrap">
                        Seller
                      </th>
                      <th className="text-left text-xs font-medium text-muted-foreground py-3 px-4 whitespace-nowrap">
                        Amount
                      </th>
                      <th className="text-left text-xs font-medium text-muted-foreground py-3 px-4 whitespace-nowrap">
                        Price
                      </th>
                      <th className="text-left text-xs font-medium text-muted-foreground py-3 px-4 whitespace-nowrap">
                        Status
                      </th>
                      <th className="text-left text-xs font-medium text-muted-foreground py-3 px-4 whitespace-nowrap">
                        Time
                      </th>
                      <th className="text-left text-xs font-medium text-muted-foreground py-3 px-4 whitespace-nowrap">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedTrades?.map((trade) => (
                      <tr
                        key={trade.id}
                        className="border-b border-border hover:bg-muted/50 dark:hover:bg-[#252A32] transition-colors"
                      >
                        <td className="py-4 px-4">
                          <span className="text-primary font-mono text-sm">
                            {trade.id.slice(0, 8)}...
                          </span>
                        </td>
                        <td className="py-4 px-4">
                          <span
                            className={`px-2 py-1 rounded text-xs ${
                              trade?.advertisement?.type === TradeType.BUY
                                ? "bg-emerald-500/10 text-emerald-500"
                                : "bg-red-500/10 text-red-500"
                            }`}
                          >
                            {trade?.advertisement?.type?.toUpperCase()}
                          </span>
                        </td>
                        <td className="py-4 px-4 text-foreground text-sm">
                          {trade.buyer?.firstName +
                            " " +
                            trade?.buyer?.lastName || "Unknown"}
                        </td>
                        <td className="py-4 px-4 text-foreground text-sm">
                          {trade.seller?.firstName +
                            " " +
                            trade?.seller?.lastName || "Unknown"}
                        </td>
                        <td className="py-4 text-foreground font-mono text-sm">
                          {Number(trade.amount).toFixed(2)}{" "}
                          {trade?.advertisement?.asset &&
                            normalizeCurrency(
                              trade?.advertisement?.asset?.currency ?? "",
                            )}
                        </td>
                        <td className="py-4 text-foreground font-mono text-sm">
                          {trade?.advertisement?.fiatCurrency?.symbol ?? ""}{" "}
                          {Number(trade.price).toFixed(2)}
                        </td>
                        <td className="py-4 px-4">
                          {getStatusBadge(trade.status)}
                        </td>
                        <td className="py-4 px-4 text-muted-foreground text-sm">
                          {new Date(trade.createdAt).toLocaleString()}
                        </td>
                        <td className="py-4 px-4">
                          <div className="flex items-center justify-center gap-2">
                            <button
                              onClick={() => {
                                setSelectedTrade(trade);
                                setShowTradeModal(true);
                              }}
                              className="p-2 text-muted-foreground hover:text-primary transition-colors"
                              title="View Details"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => {
                                setSelectedTrade(trade);
                                setCommunicationOpen(true);
                              }}
                              title="Open trade communication"
                            >
                              <MessageSquare className="h-4 w-4" />
                            </Button>
                            {/* {trade.status === TradeStatus.DISPUTED && (
                              <button
                                onClick={() => {
                                  setSelectedTrade(trade);
                                  setShowMessageModal(true);
                                }}
                                className="p-2 text-muted-foreground hover:text-emerald-500 transition-colors"
                                title="Send Message"
                              >
                                <MessageSquare className="w-4 h-4" />
                              </button>
                            )} */}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="flex items-center justify-between p-4 border-t border-border">
                <p className="text-sm text-muted-foreground">
                  Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
                  {Math.min(
                    currentPage * itemsPerPage,
                    Number(filteredTrades?.length),
                  )}{" "}
                  of {filteredTrades?.length} trades
                </p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="p-2 rounded-lg bg-card text-muted-foreground disabled:opacity-50 hover:text-foreground transition-colors"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  {Array.from(
                    { length: Math.min(5, totalPages) },
                    (_, i) => i + 1,
                  ).map((page) => (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`px-3 py-1 rounded-lg text-sm ${
                        currentPage === page
                          ? "bg-primary text-primary-foreground"
                          : "bg-card text-muted-foreground hover:text-foreground"
                      }`}
                    >
                      {page}
                    </button>
                  ))}
                  <button
                    onClick={() =>
                      setCurrentPage((p) => Math.min(totalPages, p + 1))
                    }
                    disabled={currentPage === totalPages}
                    className="p-2 rounded-lg bg-card text-muted-foreground disabled:opacity-50 hover:text-foreground transition-colors"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Trade Detail Modal */}
      {showTradeModal && selectedTrade && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-card rounded-xl p-6 max-w-lg w-full mx-4 max-h-[90vh] overflow-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-foreground">
                Trade Details
              </h3>
              <button
                onClick={() => setShowTradeModal(false)}
                className="text-muted-foreground hover:text-foreground"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="flex justify-between py-3 border-b border-border">
                <span className="text-muted-foreground">Trade ID</span>
                <span className="text-foreground font-mono">
                  {selectedTrade.id}
                </span>
              </div>
              <div className="flex justify-between py-3 border-b border-border">
                <span className="text-muted-foreground">Type</span>
                <span
                  className={`px-2 py-1 rounded text-xs ${
                    selectedTrade?.advertisement?.type === TradeType.BUY
                      ? "bg-emerald-500/10 text-emerald-500"
                      : "bg-red-500/10 text-red-500"
                  }`}
                >
                  {selectedTrade?.advertisement?.type}
                </span>
              </div>
              <div className="flex justify-between py-3 border-b border-border">
                <span className="text-muted-foreground">Status</span>
                {getStatusBadge(selectedTrade.status)}
              </div>
              <div className="flex justify-between py-3 border-b border-border">
                <span className="text-muted-foreground">Amount</span>
                <span className="text-foreground font-mono">
                  {Number(selectedTrade.amount).toFixed(2)}{" "}
                  {normalizeCurrency(
                    selectedTrade?.advertisement?.asset?.currency ?? "",
                  )}
                </span>
              </div>
              <div className="flex justify-between py-3 border-b border-border">
                <span className="text-muted-foreground">Price</span>
                <span className="text-foreground font-mono">
                  {selectedTrade?.advertisement?.fiatCurrency?.symbol}{" "}
                  {Number(selectedTrade.price).toFixed(2)}
                </span>
              </div>
              <div className="flex justify-between py-3 border-b border-border">
                <span className="text-muted-foreground">Total</span>
                <span className="text-foreground font-mono">
                  {selectedTrade?.advertisement?.fiatCurrency?.symbol}{" "}
                  {formatCurrency(
                    Number(selectedTrade.amount) * Number(selectedTrade.price),
                  )}
                </span>
              </div>
              <div className="flex justify-between py-3 border-b border-border">
                <span className="text-muted-foreground">Buyer</span>
                <span className="text-foreground">
                  {selectedTrade.buyer?.firstName +
                    " " +
                    selectedTrade?.buyer?.lastName || "Unknown"}
                </span>
              </div>
              <div className="flex justify-between py-3 border-b border-border">
                <span className="text-muted-foreground">Seller</span>
                <span className="text-foreground">
                  {selectedTrade.seller?.firstName +
                    " " +
                    selectedTrade?.seller?.lastName || "Unknown"}
                </span>
              </div>
              <div className="flex justify-between py-3">
                <span className="text-muted-foreground">Created</span>
                <span className="text-foreground">
                  {new Date(selectedTrade.createdAt).toLocaleString()}
                </span>
              </div>
            </div>

            {selectedTrade.status === TradeStatus.DISPUTED && (
              <div className="mt-6 flex gap-3">
                <button
                  onClick={() => handleResolveDispute(selectedTrade, "buyer")}
                  className="flex-1 py-2 bg-emerald-500 text-black rounded-lg font-medium hover:brightness-110 transition-all"
                >
                  Resolve for Buyer
                </button>
                <button
                  onClick={() => handleResolveDispute(selectedTrade, "seller")}
                  className="flex-1 py-2 bg-red-500 text-white rounded-lg font-medium hover:brightness-110 transition-all"
                >
                  Resolve for Seller
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Message Modal */}
      {showTradeModal && selectedTrade && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-card rounded-xl p-6 max-w-md w-full mx-4">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-foreground">
                Send Message
              </h3>
              <button
                // onClick={() => setShowMessageModal(false)}
                className="text-muted-foreground hover:text-foreground"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="mb-4">
              <p className="text-sm text-muted-foreground">
                Trade:{" "}
                <span className="text-primary font-mono">
                  {selectedTrade.id}
                </span>
              </p>
              <p className="text-sm text-muted-foreground">
                Parties:{" "}
                {selectedTrade.buyer?.firstName +
                  " " +
                  selectedTrade?.buyer?.lastName}{" "}
                &{" "}
                {selectedTrade.seller?.firstName +
                  " " +
                  selectedTrade?.seller?.lastName}
              </p>
            </div>
          </div>
        </div>
      )}

      {selectedTrade && (
        <TradeCommunicationDialog
          open={communicationOpen}
          onOpenChange={setCommunicationOpen}
          tradeId={selectedTrade.id}
          trade={selectedTrade}
          messages={selectedTrade.messages ?? []}
          readOnly
        />
      )}
    </div>
  );
};

export default AdminTrades;
